/**
 * Centralised GA4 analytics helpers.
 * All ReactGA.event calls go through here so event names and parameters
 * are defined in one place.
 */
import ReactGA from 'react-ga4';

// ─── Page tracking ───────────────────────────────────────────────────────────

/** Call on every SPA route change. Adds page_title to the hit. */
export const trackPageView = (pathname, search) => {
  ReactGA.send({
    hitType: 'pageview',
    page: pathname + search,
    title: document.title,
  });
};

// ─── User ─────────────────────────────────────────────────────────────────────

/** Set user-scoped dimensions (called after login / restore). userId is hashed/anonymous. */
export const setUserProperties = (userId, role) => {
  ReactGA.set({ user_id: userId, user_role: role });
};

// ─── Auth ─────────────────────────────────────────────────────────────────────

/** GA4 standard event */
export const trackLogin = (method = 'email') => {
  ReactGA.event('login', { method });
};

/** GA4 standard event */
export const trackSignUp = () => {
  ReactGA.event('sign_up', { method: 'email' });
};

// ─── Ad detail ────────────────────────────────────────────────────────────────

/**
 * Fired when a user reveals / clicks a phone number on a listing.
 * @param {object} ad - currentAd object
 */
export const trackPhoneReveal = (ad) => {
  ReactGA.event('phone_reveal', {
    ad_id: ad?.uniqueId || ad?._id,
    ad_title: ad?.title,
    category: ad?.category?.[0]?.key,
    sub_category: ad?.subCategory?.[0]?.name,
    city: ad?.city,
    price_from: ad?.priceFrom,
  });
};

/** GA4 standard e-commerce event */
export const trackAddToWishlist = (ad) => {
  ReactGA.event('add_to_wishlist', {
    ad_id: ad?.uniqueId || ad?._id,
    ad_title: ad?.title,
    category: ad?.category?.[0]?.key,
    city: ad?.city,
    price_from: ad?.priceFrom,
  });
};

/** Custom event — GA4 has no standard remove_from_wishlist */
export const trackRemoveFromWishlist = (ad) => {
  ReactGA.event('remove_from_wishlist', {
    ad_id: ad?.uniqueId || ad?._id,
    ad_title: ad?.title,
    category: ad?.category?.[0]?.key,
  });
};

// ─── Search & filter ──────────────────────────────────────────────────────────

/** GA4 standard event */
export const trackSearch = (term, category = '') => {
  if (!term || term.length < 2) return;
  ReactGA.event('search', { search_term: term, category });
};

export const trackFilterApplied = (filters) => {
  ReactGA.event('filter_applied', {
    category: filters.category || '',
    sub_category: filters.subCategory || '',
    city: filters.city || '',
    price_from: filters.priceFrom || '',
    price_to: filters.priceUpTo || '',
    ask_for_price: filters.askForPrice || false,
  });
};

// ─── Ad creation funnel ───────────────────────────────────────────────────────

export const trackAdFunnelStep = (step, categoryId = '') => {
  ReactGA.event('ad_create_step', { step, category_id: categoryId });
};

export const trackAdCreated = (ad) => {
  ReactGA.event('ad_created', {
    category_id: ad?.categoryId,
    sub_category_id: ad?.subCategoryId,
    city: ad?.city,
    price_from: ad?.priceFrom,
  });
};

// ─── Payment funnel ───────────────────────────────────────────────────────────

export const trackPaymentStep = (step, adsCount = 0, packageId = '') => {
  ReactGA.event('payment_step', { step, ads_count: adsCount, package_id: packageId });
};

/**
 * Fires GA4 standard `purchase` on successful payment or custom `payment_failed`.
 */
export const trackPaymentResult = (status, charge, paymentId) => {
  if (status === 'success' || status === 'Success') {
    ReactGA.event('purchase', {
      transaction_id: paymentId,
      value: charge,
      currency: 'EUR',
    });
  } else {
    ReactGA.event('payment_failed', {
      transaction_id: paymentId,
      value: charge,
      currency: 'EUR',
    });
  }
};

// ─── Contact form ─────────────────────────────────────────────────────────────

export const trackContactForm = (theme = '') => {
  ReactGA.event('contact_form_submit', { theme });
};

// ─── Comments ─────────────────────────────────────────────────────────────────

export const trackCommentAdded = (adId) => {
  ReactGA.event('comment_added', { ad_id: adId });
};
